/**
 * Billing, trial and tier feature-gate events.
 *
 * One slice of the `ANALYTICS_EVENTS` SSOT — see `../analytics.ts`, which
 * spreads every slice back into the single import surface call sites use.
 * The event NAME STRINGS are a PostHog dashboard contract: add and retire keys
 * here, never rename a value in place (see `rules/analytics.md`).
 */

export const BILLING_EVENTS = {
  // Billing (frontend)
  BILLING_TAB_VIEWED: 'Billing Tab Viewed',
  BILLING_CHECKOUT_INITIATED: 'Billing Checkout Initiated',
  BILLING_PORTAL_OPENED: 'Billing Portal Opened',
  BILLING_UPGRADE_PROMPTED: 'Billing Upgrade Prompted',
  // AI quota — soft warning at 80 % (toast); hard block at 100 % fires
  // BILLING_GATE_BLOCKED with source 'backend-402' via the 402 interceptor.
  AI_QUOTA_WARNING_SHOWN: 'Ai Quota Warning Shown',
  BILLING_PLAN_SWITCH_INITIATED: 'Billing Plan Switch Initiated',
  BILLING_DOWNGRADE_INITIATED: 'Billing Downgrade Initiated',
  BILLING_SUBSCRIPTION_REACTIVATED: 'Billing Subscription Reactivated',
  // Fired only after the server has applied an eligible promotion to the
  // workspace subscription. The event deliberately carries no promotion
  // identifier or submitted code.
  BILLING_PROMOTION_REDEEMED: 'Billing Promotion Redeemed',
  BILLING_COMPARE_FEATURES_OPENED: 'Billing Compare Features Opened',
  BILLING_GATE_BLOCKED: 'Billing Gate Blocked',
  // Fired when the post-checkout redirect lands but the workspace tier
  // hasn't flipped within our polling window — i.e. the activation webhook
  // didn't reach the backend in time. Actionable signal for "silent upgrade
  // failure" that previously presented as a false-positive success toast.
  BILLING_UPGRADE_UNCONFIRMED: 'Billing Upgrade Unconfirmed',
  // Billing (backend — fired via posthogService.capture() from webhook handlers)
  BILLING_SUBSCRIPTION_CREATED: 'Billing Subscription Created',
  BILLING_SUBSCRIPTION_CHANGED: 'Billing Subscription Changed',
  BILLING_SUBSCRIPTION_CANCELED: 'Billing Subscription Canceled',
  // Revenue signal — routed to #sales via a PostHog Destination.
  BILLING_PAYMENT_SUCCEEDED: 'Billing Payment Succeeded',
  // Ops alerts rather than product analytics, but registered here for the
  // same reason as the trial-seat pair below: the defined-vs-firing audit
  // reads this file, so an unlisted event reads as firing-but-undefined.
  // `Billing Price Unmapped` = a live sub on a price we cannot map, tier
  // preserved. `Billing Tier Unresolvable` = the same, with no trustworthy
  // tier to preserve, so no tier is written at all.
  BILLING_PRICE_UNMAPPED: 'Billing Price Unmapped',
  BILLING_TIER_UNRESOLVABLE: 'Billing Tier Unresolvable',
  // Trial seat growth (backend — fired from services/billing/seats.service.ts,
  // routed to #sales). Registered here as the analytics SSOT so the
  // defined-vs-firing audit sees them; the backend emits the literal, matching
  // every other captureBillingEvent call site.
  TRIAL_SEAT_THRESHOLD_REACHED: 'Trial Seat Threshold Reached',
  TRIAL_SEAT_CAP_BLOCKED: 'Trial Seat Cap Blocked',

  // Trial conversion warning (backend — fired from billing-webhook.service.ts
  // on Stripe's `customer.subscription.trial_will_end`, three days out).
  // Registered here for the same reason as the pair above. Carries seat_count
  // because that is the number the customer is about to be charged for.
  TRIAL_WILL_END: 'Trial Will End',

  // Fires when a TIER_POLICY feature gate denies access — route middleware,
  // service-layer guard, or frontend useCanUseFeature.
  // Properties: feature_key, current_tier, required_tier, gate_layer.
  FEATURE_GATE_HIT: 'Feature Gate Hit',
} as const;
